import { useEffect } from 'react';

import { createHeartbeatScheduler } from '../utils';

// Keeps the open session alive for as long as this workspace is mounted with a running timer.
//
// The interval is registered exactly once per session id and cleared on every teardown path:
// stop (the id becomes null), unmount, and unload.
//
// bfcache is the subtle case. `pagehide` fires both for a real unload and for a page being
// frozen into the back/forward cache — and a bfcache'd page is restored later with its
// JavaScript state intact, no remount and no new effect run. Stopping unconditionally there
// would leave a restored tab with a visibly running timer and no heartbeat behind it, which
// the server would eventually auto-stop as idle. So a persisted pagehide only pauses, and
// `pageshow` restarts it; `scheduler.start()` is idempotent, so the restore cannot stack a
// second interval.
export const useSessionHeartbeat = (sessionId: ID | null, onBeat: (sessionId: ID) => void) => {
    useEffect(() => {
        if (sessionId === null) {
            return;
        }

        const scheduler = createHeartbeatScheduler({
            setInterval: (handler, timeoutMs) => window.setInterval(handler, timeoutMs),
            clearInterval: (handle) => window.clearInterval(handle),
            onBeat: () => onBeat(sessionId),
        });

        scheduler.start();

        const handlePageHide = (event: PageTransitionEvent) => {
            // Frozen, not gone: pause and wait for pageshow
            scheduler.stop();
        };

        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                // Restored from bfcache with this effect never having been torn down
                scheduler.start();
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('pageshow', handlePageShow);

        return () => {
            scheduler.stop();
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [sessionId]);
};

export default useSessionHeartbeat;
